import React, { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, IconButton, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const DEFAULT_FALLBACK = '/dashboard/';

/**
 * History-back control used in place of breadcrumbs.
 * Goes to the previous page that opened the current route; falls back when history is empty.
 */
export default function PageBackNav({
  label = 'Back',
  fallbackPath = DEFAULT_FALLBACK,
  isDark = false,
  sx = {},
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleBack = useCallback(() => {
    // React Router sets a non-default key when there is in-app history to go back to.
    if (location.key !== 'default' && typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(fallbackPath || DEFAULT_FALLBACK);
  }, [fallbackPath, location.key, navigate]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minWidth: 0,
        ...sx,
      }}
    >
      <IconButton
        size="small"
        onClick={handleBack}
        aria-label={label}
        sx={{
          width: 34,
          height: 34,
          flexShrink: 0,
          color: isDark ? '#e2e8f0' : '#0f172a',
          bgcolor: isDark ? alpha('#fff', 0.06) : '#fff',
          border: `1px solid ${isDark ? alpha('#fff', 0.12) : alpha('#0f172a', 0.1)}`,
          boxShadow: isDark
            ? `0 4px 12px ${alpha('#000', 0.28)}`
            : `0 4px 12px ${alpha('#0f172a', 0.08)}`,
          '&:hover': {
            bgcolor: isDark ? alpha('#fff', 0.1) : alpha('#f8fafc', 1),
          },
        }}
      >
        <ArrowBackIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Typography
        component="button"
        type="button"
        onClick={handleBack}
        sx={{
          border: 'none',
          background: 'none',
          p: 0,
          m: 0,
          cursor: 'pointer',
          fontWeight: 700,
          fontSize: '0.875rem',
          color: isDark ? '#e2e8f0' : '#334155',
          textAlign: 'left',
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          '&:hover': {
            color: '#4ce1b6',
          },
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}
